/**
 * CostConsole — Phase 3.B
 *
 * Live Anthropic spend tracking. Every Claude call inside the worker
 * (currently /api/explain + /api/ask, will be expanded to picker /
 * grader integrations in 3.D) writes a usage record to R2:_console/
 * usage/<date>.jsonl. This view aggregates the last 30 days.
 *
 * Layout:
 *   - Hero strip (today / 7d avg / month projection / cache hit)
 *   - Daily cost trend (uPlot, area-filled)
 *   - By-source breakdown (ECharts donut + table)
 *   - By-model breakdown (ECharts donut)
 *   - ✨ "Explain this cost spike" overlay
 */
import { html, type TemplateResult } from "lit-html";
import * as echarts from "echarts";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { store } from "../data/store";
import type { CostSpendSnapshot, CostDailyBucket } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface ChartHandle { inst: any; el: HTMLElement | null; }
const charts = {
  trend:    { inst: null, el: null } as ChartHandle,
  bySource: { inst: null, el: null } as ChartHandle,
  byModel:  { inst: null, el: null } as ChartHandle,
};
let lastDataKey = "";

export function renderCostConsole(_onChange: () => void): TemplateResult {
  const e = store.get<CostSpendSnapshot>("costSpend");
  if (e.error)              return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data)              return emptyView();

  const c = e.data;
  const dataKey = `${c.today}/${c.series.length}/${c.window_total.requests}`;
  queueMicrotask(() => mountCharts(c, dataKey));

  return html`
    <div class="cost-console">
      ${heroStrip(c)}

      <section class="panel cost-chart-card">
        <header class="cost-chart-card__head">
          <div class="cost-chart-card__heading">
            <h3>Daily cost · ${c.window_total.days_in_window}d</h3>
            <span class="cost-chart-card__sub">
              total: $${c.window_total.cost_usd.toFixed(2)} ·
              ${c.window_total.requests.toLocaleString()} requests
            </span>
          </div>
          ${renderExplainButton({
            label: "Explain spend trend",
            context: () => buildTrendExplainContext(c),
          })}
        </header>
        <div id="cost-trend" class="cost-trend-canvas"></div>
      </section>

      <div class="cost-grid">
        <section class="panel cost-chart-card">
          <header class="cost-chart-card__head">
            <div class="cost-chart-card__heading">
              <h3>By source · ${c.window_total.days_in_window}d</h3>
              <span class="cost-chart-card__sub">
                console-worker calls (explain/ask/brief) + picker calls
              </span>
            </div>
            ${renderExplainButton({
              label: "Explain source mix",
              context: () => buildSourceExplainContext(c),
            })}
          </header>
          <div id="cost-by-source" class="cost-donut-canvas"></div>
        </section>

        <section class="panel cost-chart-card">
          <header class="cost-chart-card__head">
            <div class="cost-chart-card__heading">
              <h3>By model · ${c.window_total.days_in_window}d</h3>
              <span class="cost-chart-card__sub">
                Sonnet 4.6 default; cheaper models rotate in for routine calls
              </span>
            </div>
          </header>
          <div id="cost-by-model" class="cost-donut-canvas"></div>
        </section>
      </div>

      ${perSportCard(c)}
      ${tokensCard(c)}
    </div>
  `;
}

// =============================================================
// Hero strip — 4 metric tiles
// =============================================================
function heroStrip(c: CostSpendSnapshot): TemplateResult {
  const deltaPct = c.avg_7d_usd > 0
    ? ((c.delta_vs_7d_usd / c.avg_7d_usd) * 100)
    : null;

  const deltaCls = c.delta_vs_7d_usd > 0.20 ? "is-bad"
                : c.delta_vs_7d_usd < -0.20 ? "is-ok"
                : "is-neutral";

  const cacheRatePct = c.cache_hit_rate != null
    ? (c.cache_hit_rate * 100).toFixed(1)
    : "—";

  return html`
    <section class="cost-hero">
      <div class="cost-hero__tile">
        <span class="cost-hero__label">today</span>
        <span class="cost-hero__value">$${c.today_cost_usd.toFixed(2)}</span>
        <span class="cost-hero__sub">${c.today.slice(5)}</span>
      </div>
      <div class="cost-hero__tile">
        <span class="cost-hero__label">7d average</span>
        <span class="cost-hero__value">$${c.avg_7d_usd.toFixed(2)}</span>
        <span class="cost-hero__sub cost-hero__sub--${deltaCls}">
          ${c.delta_vs_7d_usd >= 0 ? "+" : ""}$${c.delta_vs_7d_usd.toFixed(2)}
          ${deltaPct != null ? ` · ${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(0)}%` : ""}
        </span>
      </div>
      <div class="cost-hero__tile">
        <span class="cost-hero__label">month projection</span>
        <span class="cost-hero__value">$${c.projection_month_usd.toFixed(2)}</span>
        <span class="cost-hero__sub">${c.days_remaining_in_month}d remain</span>
      </div>
      <div class="cost-hero__tile">
        <span class="cost-hero__label">cache hit</span>
        <span class="cost-hero__value">${cacheRatePct}%</span>
        <span class="cost-hero__sub">
          ${(c.window_total.cache_read_input_tokens / 1_000_000).toFixed(2)}M cached
        </span>
      </div>
      <div class="cost-hero__spacer"></div>
      ${renderExplainButton({
        label: "Explain cost state",
        context: () => buildOverallExplainContext(c),
      })}
    </section>
  `;
}

// =============================================================
// Per-sport breakdown — picker spend tagged by sport from
// notebooks/ai_pickers/usage_logger.py
// =============================================================
const SPORT_COLORS_COST: Record<string, string> = {
  NBA:     "var(--accent-orange-light)",
  WNBA:    "#2dd4bf",
  MLB:     "var(--accent-blue-light)",
  NHL:     "var(--accent-purple-light)",
  SOCCER:  "var(--accent-green-light)",
  UFC:     "var(--accent-red-light)",
  TENNIS:  "var(--accent-amber)",
  console: "var(--text-secondary)",
  UNTAGGED: "var(--text-quaternary)",
};

function perSportCard(c: CostSpendSnapshot): TemplateResult {
  const ws = c.window_by_sport ?? {};
  const entries = Object.entries(ws)
    .map(([sport, b]) => ({ sport, ...b }))
    .sort((a, b) => b.cost_usd - a.cost_usd);

  const total = entries.reduce((s, e) => s + e.cost_usd, 0);

  // No picker data yet — render the card with an inline note instead of
  // hiding it, so the user knows where to look once data accrues.
  const hasPickerData = entries.some((e) =>
    e.sport !== "console" && e.sport !== "UNTAGGED" && e.cost_usd > 0
  );

  return html`
    <section class="panel cost-by-sport">
      <header class="cost-by-sport__head">
        <div>
          <h3>By sport · ${c.window_total.days_in_window}d</h3>
          <span class="cost-by-sport__sub">
            picker calls tagged with sport · "console" = explain/ask/brief
          </span>
        </div>
        <span class="cost-by-sport__total">
          $${total.toFixed(2)} · ${entries.reduce((s, e) => s + e.requests, 0).toLocaleString()} calls
        </span>
      </header>

      ${!hasPickerData ? html`
        <div class="cost-by-sport__empty">
          No picker spend logged yet. Pickers tag each Anthropic call with
          its sport via <code>notebooks/ai_pickers/usage_logger.py</code>;
          the next picker run will populate this card.
        </div>
      ` : ""}

      ${entries.length === 0 ? html`
        <div class="cost-by-sport__empty">No spend in this window.</div>
      ` : html`
        <ul class="cost-by-sport__list">
          ${entries.map((e) => sportRow(e, total))}
        </ul>
      `}
    </section>
  `;
}

function sportRow(
  e: { sport: string; requests: number; cost_usd: number;
        input_tokens: number; output_tokens: number },
  total: number,
): TemplateResult {
  const pct = total > 0 ? (e.cost_usd / total) * 100 : 0;
  const color = SPORT_COLORS_COST[e.sport] ?? "var(--text-secondary)";
  const avgPerCall = e.requests > 0 ? e.cost_usd / e.requests : 0;
  return html`
    <li class="cost-by-sport__row">
      <span class="cost-by-sport__name" style="color:${color};">${e.sport}</span>
      <div class="cost-by-sport__bar-wrap">
        <div class="cost-by-sport__bar"
              style="width:${pct.toFixed(2)}%; background:${color};"></div>
      </div>
      <span class="cost-by-sport__cost">$${e.cost_usd.toFixed(3)}</span>
      <span class="cost-by-sport__pct">${pct.toFixed(0)}%</span>
      <span class="cost-by-sport__calls">${e.requests} calls</span>
      <span class="cost-by-sport__avg">
        $${avgPerCall.toFixed(4)}/call ·
        ${(e.input_tokens / 1000).toFixed(0)}k in /
        ${(e.output_tokens / 1000).toFixed(0)}k out
      </span>
    </li>
  `;
}

function tokensCard(c: CostSpendSnapshot): TemplateResult {
  const t = c.window_total;
  return html`
    <section class="panel cost-tokens">
      <h4>Tokens · ${c.window_total.days_in_window}d window</h4>
      <div class="cost-tokens__grid">
        ${tokensCell("input",      t.input_tokens,                 "var(--accent-blue-light)")}
        ${tokensCell("cache write", t.cache_creation_input_tokens, "var(--accent-amber)")}
        ${tokensCell("cache read", t.cache_read_input_tokens,      "var(--accent-green-light)")}
        ${tokensCell("output",     t.output_tokens,                "var(--accent-purple-light)")}
      </div>
    </section>
  `;
}
function tokensCell(label: string, n: number, color: string): TemplateResult {
  return html`
    <div class="cost-tokens__cell">
      <span class="cost-tokens__label" style="color:${color};">${label}</span>
      <span class="cost-tokens__value">${(n / 1_000_000).toFixed(2)}M</span>
      <span class="cost-tokens__sub">${n.toLocaleString()} tok</span>
    </div>
  `;
}

// =============================================================
// Charts
// =============================================================
function mountCharts(c: CostSpendSnapshot, dataKey: string): void {
  const newTrend    = document.getElementById("cost-trend");
  const newBySource = document.getElementById("cost-by-source");
  const newByModel  = document.getElementById("cost-by-model");
  if (!newTrend || !newBySource || !newByModel) return;

  // Tab-switch protection: dispose any chart instance whose host
  // element changed (DOM was unmounted then remounted into a fresh
  // node). Otherwise the chart would keep rendering into a detached
  // node and the visible canvas stays blank.
  const trendStale     = charts.trend.el    && charts.trend.el    !== newTrend;
  const bySourceStale  = charts.bySource.el && charts.bySource.el !== newBySource;
  const byModelStale   = charts.byModel.el  && charts.byModel.el  !== newByModel;
  if (trendStale)    { charts.trend.inst?.destroy?.(); charts.trend.inst = null; }
  if (bySourceStale) { charts.bySource.inst?.dispose(); charts.bySource.inst = null; }
  if (byModelStale)  { charts.byModel.inst?.dispose();  charts.byModel.inst  = null; }
  if (trendStale || bySourceStale || byModelStale) lastDataKey = "";

  charts.trend.el    = newTrend;
  charts.bySource.el = newBySource;
  charts.byModel.el  = newByModel;

  if (dataKey === lastDataKey) return;
  lastDataKey = dataKey;

  // ---- uPlot trend chart ----
  if (charts.trend.inst) charts.trend.inst.destroy();
  const xs   = c.series.map((d) => Date.parse(d.date) / 1000);
  const ys   = c.series.map((d) => d.cost_usd);
  charts.trend.inst = new uPlot(
    {
      width:  charts.trend.el.clientWidth,
      height: 220,
      cursor: { drag: { x: false, y: false } },
      legend: { show: false },
      scales: { x: { time: true } },
      axes: [
        {
          stroke: "#6e6e73", grid: { stroke: "#1c1c1e" },
          values: (_self, ticks) => ticks.map((t) =>
            new Date(t * 1000).toLocaleDateString([], { month: "short", day: "numeric" })),
        },
        {
          stroke: "#6e6e73", grid: { stroke: "#1c1c1e" },
          values: (_self, ticks) => ticks.map((t) => `$${t.toFixed(2)}`),
        },
      ],
      series: [
        { label: "date" },
        {
          label:  "cost",
          stroke: "#a5a3f0",
          width:  2,
          fill:   "rgba(165, 163, 240, 0.14)",
          points: { show: true, size: 6, fill: "#a5a3f0" },
        },
      ],
    },
    [xs, ys],
    charts.trend.el,
  );

  // ---- ECharts donuts ----
  if (!charts.bySource.inst) charts.bySource.inst = echarts.init(charts.bySource.el, "dark");
  if (!charts.byModel.inst)  charts.byModel.inst  = echarts.init(charts.byModel.el, "dark");

  charts.bySource.inst.setOption(donutOption(aggregateBy(c.series, "by_source"), "by source"), true);
  charts.byModel.inst.setOption(donutOption(aggregateBy(c.series, "by_model"),  "by model"),  true);

  if (!(window as any).__costResize) {
    (window as any).__costResize = true;
    window.addEventListener("resize", () => {
      charts.trend.inst?.setSize({ width: charts.trend.el!.clientWidth, height: 220 });
      charts.bySource.inst?.resize();
      charts.byModel.inst?.resize();
    });
  }
}

function aggregateBy(
  series: CostDailyBucket[],
  field: "by_source" | "by_model",
): { name: string; value: number }[] {
  const totals: Record<string, number> = {};
  for (const day of series) {
    for (const [k, v] of Object.entries(day[field] ?? {})) {
      totals[k] = (totals[k] ?? 0) + v.cost_usd;
    }
  }
  return Object.entries(totals)
    .filter(([, v]) => v > 0.0001)
    .map(([name, value]) => ({ name, value: Number(value.toFixed(4)) }))
    .sort((a, b) => b.value - a.value);
}

function donutOption(data: { name: string; value: number }[], _title: string): echarts.EChartsOption {
  const total = data.reduce((s, d) => s + d.value, 0);
  return {
    backgroundColor: "transparent",
    color: ["#a5a3f0", "#5fa9ff", "#5fd47f", "#ffb43d", "#d39bff", "#ff6b6b"],
    legend: {
      bottom: 6,
      textStyle: { color: "#a1a1a6", fontSize: 11 },
    },
    tooltip: {
      backgroundColor: "rgba(20, 20, 22, 0.96)",
      borderColor: "rgba(255,255,255,0.10)",
      textStyle: { color: "#e6e6eb", fontSize: 12 },
      formatter: (p: any) =>
        `<b>${p.name}</b><br>$${p.value.toFixed(4)} (${(p.value / total * 100).toFixed(1)}%)`,
    },
    series: [{
      type: "pie",
      radius: ["54%", "76%"],
      avoidLabelOverlap: true,
      label: {
        show: true,
        formatter: (p: any) => `${p.name}\n$${p.value.toFixed(2)}`,
        color: "#a1a1a6", fontSize: 10,
      },
      labelLine: { show: false },
      data: data.length > 0 ? data : [{ name: "no spend yet", value: 1, itemStyle: { color: "#3a3a3c" } }],
    } as echarts.PieSeriesOption],
  } as echarts.EChartsOption;
}

// =============================================================
// Explain context builders
// =============================================================
function buildOverallExplainContext(c: CostSpendSnapshot): string {
  return [
    `Cost state as of ${c.today}:`,
    `Today: $${c.today_cost_usd.toFixed(2)} | 7d avg: $${c.avg_7d_usd.toFixed(2)} (delta ${c.delta_vs_7d_usd >= 0 ? "+" : ""}$${c.delta_vs_7d_usd.toFixed(2)})`,
    `Month projection: $${c.projection_month_usd.toFixed(2)} (${c.days_remaining_in_month} days remaining)`,
    `Cache hit rate: ${c.cache_hit_rate != null ? (c.cache_hit_rate * 100).toFixed(1) + "%" : "no cached calls yet"}`,
    `Window: ${c.window_total.days_in_window}d, ${c.window_total.requests} requests, $${c.window_total.cost_usd.toFixed(2)} total`,
    "",
    "Tokens (window):",
    `  input: ${(c.window_total.input_tokens / 1_000_000).toFixed(2)}M`,
    `  cache write: ${(c.window_total.cache_creation_input_tokens / 1_000_000).toFixed(2)}M`,
    `  cache read: ${(c.window_total.cache_read_input_tokens / 1_000_000).toFixed(2)}M`,
    `  output: ${(c.window_total.output_tokens / 1_000_000).toFixed(2)}M`,
    "",
    "NOTE: This is console-worker spend only (/api/explain, /api/ask).",
    "Picker / grader / calibration spend is NOT yet tracked — that lands in Phase 3.D.",
  ].join("\n");
}

function buildTrendExplainContext(c: CostSpendSnapshot): string {
  return [
    `Daily cost trend over last ${c.window_total.days_in_window}d:`,
    ...c.series.slice(-10).map((d) =>
      `  ${d.date}: $${d.cost_usd.toFixed(2)} (${d.requests} req)`),
  ].join("\n");
}

function buildSourceExplainContext(c: CostSpendSnapshot): string {
  const byS = aggregateBy(c.series, "by_source");
  return [
    `Spend by source over ${c.window_total.days_in_window}d:`,
    ...byS.map((s) => `  ${s.name}: $${s.value.toFixed(4)}`),
  ].join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="cost-console">
      <section class="cost-hero">
        ${Array.from({ length: 4 }, () => html`
          <div class="cost-hero__tile">
            <div class="skeleton" style="height:14px;width:60%;"></div>
            <div class="skeleton" style="height:24px;width:80%;margin-top:8px;"></div>
          </div>
        `)}
      </section>
      <section class="panel" style="height:220px;"></section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        <p style="font-size:15px;color:var(--text-primary);margin:0 0 8px;">
          No usage records yet
        </p>
        <p style="margin:0;">
          The cost tracker logs every Claude call from the console-worker
          (<code style="color:var(--accent-blue-light);">/api/explain</code> and
          <code style="color:var(--accent-blue-light);">/api/ask</code>) to
          R2 and aggregates here. Use the ✨ buttons or Cmd-K once and the
          first record will appear.
        </p>
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Cost Console</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
